// ─── src/api/awardsInvitations.ts ────────────────────────────────────────────
//
// Admin API for the Startup Syria Awards 2026 guest list: upload, send,
// remind and track each guest's personal invitation link.

import { apiClient } from "./registration";

export type InvitationStatus = "pending" | "sent" | "opened" | "confirmed" | "declined";
/** Guest group; sets the RSVP deadline and the WhatsApp message. */
export type GuestCategory = "catalyst" | "partner" | "vip";

export interface AwardsInvitation {
  id: string;
  fullName: string;
  fullNameEn: string | null;
  /** null for guests added by name only — their link is shared by hand. */
  email: string | null;
  phone: string | null;
  lang: "ar" | "en";
  /** Picks السيد / السيدة (Mr. / Ms.). Null = neutral salutation. */
  gender: "male" | "female" | null;
  category: GuestCategory | null;
  status: InvitationStatus;
  /** The guest's personal page — share only with that guest. */
  link: string;
  emailSentAt: string | null;
  lastEmailError: string | null;
  reminderSentAt: string | null;
  reminderCount: number;
  whatsappSentAt: string | null;
  openedAt: string | null;
  openCount: number;
  rsvp: "confirmed" | "declined" | null;
  rsvpAt: string | null;
  createdAt: string;
}

export interface InvitationsPayload {
  items: AwardsInvitation[];
  stats: {
    total: number;
    pending: number;
    sent: number;
    opened: number;
    confirmed: number;
    declined: number;
    emailed: number;
    whatsapped: number;
    emailErrors: number;
    noEmail?: number;
  };
  /** Bulk email job in progress, if any. */
  job: { kind: "invite" | "remind"; total: number; done: number; failed: number } | null;
  rsvpClosesAt: string;
}

export interface InvitationRow {
  fullName: string;
  fullNameEn?: string;
  email?: string;
  phone?: string;
  lang?: "ar" | "en";
  gender?: "male" | "female";
  category?: GuestCategory;
}

export interface ImportResult {
  created: number;
  updated: number;
  skipped: Array<{ row: number; email: string; reason: string }>;
}

export const awardsInvitationsApi = {
  list: () => apiClient.get<InvitationsPayload>("/awards-invitations").then((r) => r.data),
  import: (rows: InvitationRow[]) =>
    apiClient
      .post<ImportResult>("/awards-invitations/import", { rows }, { timeout: 60_000 })
      .then((r) => r.data),
  /** No ids = everyone not emailed yet. */
  send: (ids?: string[]) =>
    apiClient.post<{ queued: number }>("/awards-invitations/send", { ids }).then((r) => r.data),
  remind: () =>
    apiClient.post<{ queued: number }>("/awards-invitations/remind").then((r) => r.data),
  update: (
    id: string,
    patch: Partial<{
      fullName: string;
      fullNameEn: string | null;
      email: string | null;
      phone: string | null;
      lang: "ar" | "en";
      gender: "male" | "female" | null;
      category: GuestCategory | null;
      whatsappSent: boolean;
      rsvp: "confirmed" | "declined" | null;
    }>,
  ) => apiClient.patch<AwardsInvitation>(`/awards-invitations/${id}`, patch).then((r) => r.data),
  remove: (id: string) => apiClient.delete(`/awards-invitations/${id}`).then((r) => r.data),
};
